/**
 * Sequoia's tree, in the shared 120×120 LINE asset box.
 *
 * The theme's hero object is the giant redwood itself: three stacked tiers over
 * a short trunk, apexes softened so it reads cute rather than like a road sign.
 * It is drawn in TWO places — the passcode dots (line-passcode) and the Home tab
 * button (line-menu-button) — so the geometry lives here and both import it, the
 * same rule the Everest summit and the Aurora orb follow. Copies drift; a shared
 * module cannot.
 */

/** One tier: a triangle with a rounded apex, sitting on `baseY`. */
function tier( cx: number, apexY: number, halfW: number, baseY: number ): string {
  const s = halfW * 0.16;
  return `M${ cx - halfW },${ baseY }`
    + ` L${ cx - s },${ apexY + 7 }`
    + ` Q${ cx },${ apexY - 5 } ${ cx + s },${ apexY + 7 }`
    + ` L${ cx + halfW },${ baseY } Z`;
}

/** The canopy: three tiers, widest at the bottom. One path, so a single fill
 *  unions them and the overlaps leave no seam. */
export const TREE_CANOPY = [
  tier( 60, 16, 20, 48 ),
  tier( 60, 38, 29, 76 ),
  tier( 60, 62, 38, 102 ),
].join( ' ' );

/** The trunk below the canopy. */
export const TREE_TRUNK = 'M53,100 L67,100 L67,114 Q67,117 64,117 L56,117 Q53,117 53,114 Z';

/** Sequoia's greens, darkest to brightest. */
export const TREE_DARK = '#3C5330';
export const TREE_MID = '#5E8B45';
export const TREE_LIGHT = '#8CC63F';
/** Bark. */
export const TREE_BARK = '#6D4130';

/**
 * A single LEAF — a classic pointed oval with a midrib and veins, for the
 * passcode dots (owner 2026-08-13: the needled frond drawn before this read as
 * too thin at dot size, "klasik yaprak daha iyi olur"). The Home tab keeps the
 * tree; this is the theme's foliage at a size where a broad shape reads.
 *
 * Built from a straight axis with a lens-shaped half-width, so the blade comes
 * to a clean point at both ends.
 */
function leafParts(): { blade: string; veins: string } {
  const x0 = 26, y0 = 98, x1 = 96, y1 = 24;     // base -> tip
  const half = 21;                              // widest half-width, mid-blade
  const N = 26;
  const f = ( v: number ) => +v.toFixed( 1 );
  const dx = x1 - x0, dy = y1 - y0;
  const L = Math.hypot( dx, dy );
  const ux = dx / L, uy = dy / L;               // along the blade
  const vx = -uy, vy = ux;                      // across it
  const at = ( t: number ) => [ x0 + dx * t, y0 + dy * t ];
  const top: string[] = [];
  const bot: string[] = [];
  for ( let i = 0; i <= N; i++ ) {
    const t = i / N;
    const [ px, py ] = at( t );
    // Widest a little past the middle, like a real leaf.
    const h = half * Math.pow( Math.sin( Math.PI * Math.pow( t, 0.86 ) ), 0.72 );
    top.push( `${ f( px + vx * h ) },${ f( py + vy * h ) }` );
    bot.push( `${ f( px - vx * h ) },${ f( py - vy * h ) }` );
  }
  const blade = `M${ top[ 0 ] } L${ top.slice( 1 ).join( ' L' ) } L${ bot.reverse().join( ' L' ) } Z`;
  // Midrib plus veins branching off it toward the edges.
  const veins = [ `M${ f( x0 ) },${ f( y0 ) } L${ f( x1 ) },${ f( y1 ) }` ];
  for ( let i = 1; i <= 5; i++ ) {
    const t = 0.16 + ( i / 6 ) * 0.66;
    const [ px, py ] = at( t );
    const h = half * Math.pow( Math.sin( Math.PI * Math.pow( t, 0.86 ) ), 0.72 ) * 0.78;
    for ( const side of [ 1, -1 ] ) {
      const ex = px + ( vx * side + ux * 0.55 ) * h;
      const ey = py + ( vy * side + uy * 0.55 ) * h;
      parts_push( veins, px, py, ex, ey, f );
    }
  }
  return { blade, veins: veins.join( ' ' ) };
}
function parts_push( out: string[], px: number, py: number, ex: number, ey: number,
                     f: ( v: number ) => number ): void {
  out.push( `M${ f( px ) },${ f( py ) } L${ f( ex ) },${ f( ey ) }` );
}
const LEAF_PARTS = leafParts();
/** The leaf blade, base bottom-left to tip top-right. */
export const LEAF_BLADE = LEAF_PARTS.blade;
/** Its midrib and veins, drawn as strokes over the blade. */
export const LEAF_VEINS = LEAF_PARTS.veins;

/**
 * A single sequoia FROND — the sprig the theme's own icon is drawn from: a
 * curved stem with paired needles sweeping toward the tip. Used for the passcode
 * dots (owner 2026-08-13: single leaves there, not whole trees), while the Home
 * tab keeps the tree above. Same relationship Sakura has between its blossom and
 * its drifting petals.
 *
 * Built rather than hand-written so the pairs stay even and the needles shorten
 * toward the tip on their own.
 */
function frondPath( n: number, x0: number, y0: number, x1: number, y1: number,
                    needle: number, bow: number ): string {
  const f = ( v: number ) => +v.toFixed( 1 );
  // Stem: a quadratic, bowed so the frond curves like a real sprig.
  const cx = ( x0 + x1 ) / 2 + bow;
  const cy = ( y0 + y1 ) / 2 + bow * 0.4;
  const parts = [ `M${ x0 },${ y0 } Q${ f( cx ) },${ f( cy ) } ${ x1 },${ y1 }` ];
  const at = ( t: number ): [ number, number ] => {
    const u = 1 - t;
    return [
      u * u * x0 + 2 * u * t * cx + t * t * x1,
      u * u * y0 + 2 * u * t * cy + t * t * y1,
    ];
  };
  for ( let i = 0; i < n; i++ ) {
    const t = 0.08 + ( i / ( n - 1 ) ) * 0.88;
    const [ px, py ] = at( t );
    const [ nx, ny ] = at( Math.min( 1, t + 0.01 ) );
    const dx = nx - px, dy = ny - py;
    const l = Math.hypot( dx, dy ) || 1;
    // Needles shorten toward the tip and sweep forward along the stem.
    const len = needle * ( 1 - t * 0.55 );
    const ux = dx / l, uy = dy / l;          // along the stem
    const vx = -uy, vy = ux;                 // across it
    const sweep = 0.42;                      // how far forward the needles lean
    for ( const side of [ 1, -1 ] ) {
      const ex = px + ( vx * side + ux * sweep ) * len;
      const ey = py + ( vy * side + uy * sweep ) * len;
      parts.push( `M${ f( px ) },${ f( py ) } L${ f( ex ) },${ f( ey ) }` );
    }
  }
  return parts.join( ' ' );
}

/** The frond, in the 120 box: base bottom-left, tip top-right. */
export const FROND = frondPath( 11, 26, 102, 96, 24, 15, -13 );
